import { ClientLocation } from "Util/clientLocation";
import { Emitter } from "Util/emitter.js";
import markerSvg from "Images/icons/location/strokedMarker.txtsvg";
import * as atlas from "azure-maps-control";

export class PcMapsConfig {
    constructor(sasToken, emitter = null) {
        this.MapConfig = {
            authOptions: {
                authType: "sas",
                sasToken: sasToken //Your Azure Maps client id for accessing your Azure Maps account.
            }
        };
        this.Emitter = emitter ?? new Emitter();
        this.OnReady = null;
        this.OnSetup = null;
    }
}

/**
 *
 * @param {String} sasToken SAS token for Azure Maps authentication
 * @param {Emitter} emitter Emitter to use for event handling
 * @param {ClientLocation} fallbackCenter A center to use if no initial location is provided
 * @param {ClientLocation} initialLocation Initial location for marker, if one exists
 */
export function GetLocationConfig(sasToken, emitter, fallbackCenter, initialLocation) {
    const locationConfig = new PcMapsConfig(sasToken, emitter);

    const initialLocationDefined = initialLocation?.isFullyDefined ?? false;

    locationConfig.MapConfig.center = initialLocationDefined
        ? initialLocation.toAzurePointArray()
        : fallbackCenter?.toAzurePointArray();
    locationConfig.MapConfig.zoom = initialLocationDefined
        ? 15 // 4.8 m per pixel
        : 6; // 2.4 km per pixel
    locationConfig.MapConfig.style = "satellite_road_labels";
    locationConfig.MapConfig.showLogo = false;

    const dataMarkerHtml = `<div class="marker-container"><div class="data-marker">${markerSvg}</div></div>`;
    const chosenMarkerHtml = `<div class="marker-container"><div class="select-marker">${markerSvg}</div><div class="marker-pulse" ></div></div>`;

    let dataMarker = null;
    let chosenMarker = null;
    let mapObject;

    function updateChosenMarker(map, position) {
        const chosenMarkerOptions = {
            htmlContent: chosenMarkerHtml,
            position: position
        };

        // Show or update marker
        if (!chosenMarker) {
            chosenMarker = new atlas.HtmlMarker(chosenMarkerOptions);
            map.markers.add(chosenMarker);
        } else {
            chosenMarker.setOptions(chosenMarkerOptions);
        }
    }

    locationConfig.OnSetup = function (map) {
        mapObject = map;

        // add controls
        map.controls.add(new atlas.control.ZoomControl(), {
            position: "bottom-left"
        });
        const styleControl = new atlas.control.StyleControl({
            mapStyles: ["satellite_road_labels", "road_shaded_relief", "road"]
        });
        map.controls.add(styleControl, {
            position: "bottom-left"
        });

        // change style
        map.getCanvas().style.cursor = "pointer";
        map.events.add("dragstart", function () {
            map.getCanvas().style.cursor = "grabbing";
        });
        map.events.add("dragend", function () {
            map.getCanvas().style.cursor = "pointer";
        });
    };

    locationConfig.OnReady = function (map) {
        mapObject = map;
        map.events.add("click", function (e) {
            // Let consumer know the chosen location has changed
            const chosenLocation = new ClientLocation(e.position[0], e.position[1]);
            locationConfig.Emitter.emit("chosen-location-change", chosenLocation);
        });
        locationConfig.Emitter.emit("map-ready");
    };

    emitter.on("clear-map-chosen-marker", () => {
        if (mapObject && chosenMarker) {
            mapObject.markers.remove(chosenMarker);
            chosenMarker = null;
        }
    });

    emitter.on("update-map-chosen-marker", (newLocation) => {
        if (mapObject && newLocation.isFullyDefined) {
            updateChosenMarker(mapObject, newLocation.toAzurePointArray());
        }
    });

    emitter.on("clear-data-marker", () => {
        if (mapObject && dataMarker) {
            mapObject.markers.remove(dataMarker);
            dataMarker = null;
        }
    });

    emitter.on("update-data-marker", (newLocation) => {
        if (mapObject) {
            const newLocationCoords = newLocation.toAzurePointArray();
            if (dataMarker) {
                const existingOptions = dataMarker.getOptions();
                dataMarker.setOptions({
                    ...existingOptions,
                    position: newLocationCoords
                });
            } else {
                dataMarker = new atlas.HtmlMarker({
                    htmlContent: dataMarkerHtml,
                    position: newLocationCoords
                });

                mapObject.markers.add(dataMarker);
            }

            mapObject.setCamera({
                center: newLocationCoords,
                zoom: 15,
                duration: 3000,
                type: "fly"
            });
        }
    });

    emitter.on("update-sas-token", (newSasToken) => {
        locationConfig.MapConfig.authOptions.sasToken = newSasToken;
    });

    return locationConfig;
}
